import '../../components.scss';

import { RootState, useAppDispatch } from '_store';
import { getTopologyType } from '_store/application/hooks';
import { pmvL10nArgs } from '_store/application/pmvLabels';
import applicationReducer from '_store/application/reducer';
import { themeSelector } from '_store/application/selectors';
import { AppTheme, DialogType, TopologyType } from '_store/application/types';
import { useDenmsRequest } from '_store/denm/utils';
import { devicesSagas } from '_store/devices/sagas';
import { useIvimsRequest } from '_store/ivim/utils';
import { pmvsSagas } from '_store/pmv/sagas';
import roadsReducer from '_store/roads/roadsReducers';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { spatemsSagas } from '_store/spatem/spatemsSagas';
import { useLocalization } from '@fluent/react';
import { faCircleHalfStroke } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@material-ui/core';
import { RouteComponentProps } from '@reach/router';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { MapContainer, Polyline } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import { useSelector } from 'react-redux';

import { getFromLocalStorage, updateLocalStorage } from '../../../../utils';
import { UnifiedMarkerCluster } from './clustering';
import Denms from './Elements/Denms';
import Devices from './Elements/Devices';
import ExtraSelectionElements from './Elements/ExtraSelectionElements';
import Ivims from './Elements/Ivims';
import Pmvs from './Elements/Pmvs';
import RenderPath from './Elements/renderPath';
import Roads from './Elements/Roads';
import SelectionElements from './Elements/SelectionElements';
import Spatems from './Elements/Spatems';
import Vehicles from './Elements/Vehicles';
import MapEvents from './Events/MapEvents';
import HeatmapLayer from './HeatmapLayer';
import useMap from './hooks/useMap';
import { darkModeStyle, lightModeStyle } from './mapStyles';
import styles from './styles';
import { toLatLng } from './utils';

const enum MapType {
  ROADMAP = 'roadmap',
  HYBRID = 'hybrid',
  TERRAIN = 'terrain',
  SATELLITE = 'satellite',
}

const isUrbanTopology = getTopologyType() === TopologyType.URBAN;
const layers = ['event', 'informative', 'rsu', 'pmv', ...(isUrbanTopology ? ['spatem'] : []), 'data'];

const GOOGLE_MAPS_CALLBACK = '__googleMapsCallback_emeras';

const MapView: FC = (_props: RouteComponentProps): ReactElement => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const theme = useSelector(themeSelector);
  const mapTypeFromLocalStorage = useMemo(
    () => (getFromLocalStorage('mapType') as MapType) ?? MapType.ROADMAP,
    [],
  );

  const [mapType, setMapType] = useState(mapTypeFromLocalStorage);
  const updateMapType = (e) => {
    updateLocalStorage('mapType', e.target.value);
    setMapType(e.target.value);
  };

  const updateTheme = (e) => {
    const newTheme = e.target.checked ? AppTheme.DARK : AppTheme.LIGHT;
    updateLocalStorage('appTheme', newTheme);
    dispatch(
      applicationReducer.actions.setTheme({
        theme: newTheme,
      }),
    );
  };

  const {
    roads,
    devices,
    pmvs,
    denms,
    ivim,
    vehicles,
    spatems,
    dialog,
    extra,
    selection,
    activeLayers,
    mapRef,
    loading,
    links,
    loadHandler,
    meterPerPixel,
    centerCoordinates,
    googleMapsApiKey,
    zoom,
  } = useMap();

  // Load Google Maps API with loading=async (best practice); then the layer uses useGoogMapsLoader: false
  useEffect(() => {
    const w = typeof window !== 'undefined' ? window : null;
    const hasGoogle = w && (w as Window & { google?: { maps?: unknown } }).google?.maps;
    if (!googleMapsApiKey || hasGoogle) return;
    if (document.getElementById('google-maps-script-emeras')) return;
    const callbackName = GOOGLE_MAPS_CALLBACK;
    (window as unknown as Record<string, () => void>)[callbackName] = function onGoogleMapsLoaded() {
      (window as unknown as Record<string, () => void>)[callbackName] = function noop() {
        /* API loaded */
      };
    };
    const params = new URLSearchParams({
      key: googleMapsApiKey,
      loading: 'async',
      v: '3.64',
      language: 'it',
      region: 'IT',
      callback: callbackName,
    });
    const script = document.createElement('script');
    script.id = 'google-maps-script-emeras';
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [googleMapsApiKey]);

  const dispatchDenmsRequest = useDenmsRequest({
    currentPage: 1,
  });
  const dispatchIvimsRequest = useIvimsRequest({ currentPage: 1 });
  const selectedRoadSegmentIds = useSelector(selectedRoadSegmentIdsSelector);

  const denmFormState = useSelector((state: RootState) => state.forms['manage-denm']);

  const updateSelectedRoad = (e) => {
    const selectedRoads = e.target.value as string[];

    // Prevent deselection if there is only one road selected
    if (selectedRoads.length === 0 && selectedRoadSegmentIds.length === 1) {
      return;
    }

    updateLocalStorage('selectedRoadIds', JSON.stringify(selectedRoads));

    dispatch(roadsReducer.actions.selectRoad(selectedRoads));
    dispatch(devicesSagas.actions.devicesRequest({ roadSegmentIds: selectedRoads }));
    dispatch(pmvsSagas.actions.vmsRequest());
    if (isUrbanTopology) {
      dispatch(spatemsSagas.actions.spatemsRequest({ roadSegmentIds: selectedRoads }));
    }

    dispatchDenmsRequest(selectedRoads);
    dispatchIvimsRequest(selectedRoads);
  };

  const mapStyle = theme === AppTheme.DARK ? darkModeStyle : lightModeStyle;

  return (
    <Card className={classes.cardStyle}>
      <CardContent className={classes.cardContent}>
        {loading ? (
          <Typography>{l10n.getString('map-loading')}</Typography>
        ) : (
          <MapContainer
            className={classes.mapContainer}
            zoom={zoom}
            center={centerCoordinates}
            scrollWheelZoom={true}
            doubleClickZoom={false}
            attributionControl={false}
            whenCreated={loadHandler}
            // onClick={pointClicked}
          >
            <ReactLeafletGoogleLayer
              key={`${mapType}-${theme}`}
              type={mapType}
              continuousWorld={true}
              apiKey={googleMapsApiKey}
              useGoogMapsLoader={false}
              googleMapsLoaderConf={{
                version: '3.64',
                language: 'it',
                region: 'IT',
                apiKey: googleMapsApiKey,
              }}
              styles={mapStyle}
            />
            <div className={classes.mapButtons}>
              <ButtonGroup size="large" disableElevation variant="contained">
                {layers.map((layer) => (
                  <Button
                    key={layer}
                    className={activeLayers[layer] ? 'Map-buttons-sel' : 'Map-buttons'}
                    onClick={() => {
                      updateLocalStorage(layer, !activeLayers[layer]);
                      dispatch(applicationReducer.actions.toggleActiveLayer(layer));
                    }}
                  >
                    {layer === 'pmv'
                      ? l10n.getString('map-filter-pmv', pmvL10nArgs())
                      : l10n.getString(`map-filter-${layer}`)}
                  </Button>
                ))}
              </ButtonGroup>
              <div>
                <Select
                  className="Map-Road-select"
                  onChange={updateSelectedRoad}
                  value={selectedRoadSegmentIds ?? []}
                  multiple
                  variant="outlined"
                >
                  {roads.map((road, key) => (
                    <MenuItem key={key} value={road.id}>
                      {road.name}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  className="Map-Type-select"
                  onChange={updateMapType}
                  value={mapType}
                  variant="outlined"
                >
                  {[MapType.ROADMAP, MapType.HYBRID, MapType.TERRAIN, MapType.SATELLITE].map(
                    (type, key) => (
                      <MenuItem key={key} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)} map
                      </MenuItem>
                    ),
                  )}
                </Select>
              </div>
            </div>
            {mapRef && (
              <>
                {roads && <Roads roads={roads} />}
                <UnifiedMarkerCluster>
                  {devices && activeLayers.rsu && <Devices devices={devices} mapRef={mapRef} />}
                  {pmvs && activeLayers.pmv && <Pmvs pmvs={pmvs} mapRef={mapRef} />}
                  {spatems && activeLayers.spatem && <Spatems spatems={spatems} mapRef={mapRef} />}
                  {denms && activeLayers.event && <Denms denms={denms} mapRef={mapRef} />}
                  {ivim && activeLayers.informative && <Ivims ivims={ivim} mapRef={mapRef} />}
                </UnifiedMarkerCluster>
                {vehicles && activeLayers.data && <Vehicles vehicles={vehicles} mapRef={mapRef} />}
              </>
            )}

            {selection.style && <SelectionElements selection={selection} />}

            <ExtraSelectionElements extra={extra} />

            <MapEvents />

            {/* TODO: reimplement this stuff */}
            {mapRef &&
              Object.entries(links).map(([id, l]) => (
                <Polyline
                  key={`link-${id}`}
                  positions={l
                    .filter((n) => n.loc && n.loc.coordinates)
                    .map((n) => toLatLng(n.loc.coordinates))}
                  weight={1}
                  color="#0ff"
                  // geodesic={true}
                />
              ))}

            {dialog && mapRef && (
              <RenderPath
                dialog={dialog}
                denms={activeLayers.event && denms}
                ivim={activeLayers.informative && ivim}
                meterPerPixel={meterPerPixel}
                createDenmGeoNetworkingDistance={
                  dialog.type === DialogType.DENM_LIST &&
                  denmFormState?.optional?.geo_networking?.distance_a
                    ? denmFormState.optional.geo_networking.distance_a
                    : undefined
                }
                referencePoint={
                  dialog.type === DialogType.DENM_LIST && denmFormState?.referencePoint
                    ? denmFormState.referencePoint
                    : undefined
                }
              />
            )}
          </MapContainer>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={theme === AppTheme.DARK}
              onChange={updateTheme}
              name="darkModeSwitch"
              color="primary"
              size="small"
            />
          }
          label={<FontAwesomeIcon icon={faCircleHalfStroke} size="sm" />}
          className={classes.themeSwitch}
        />

        {!loading && <HeatmapLayer map={mapRef} />}
      </CardContent>
    </Card>
  );
};

export default React.memo(MapView);
